import React from 'react';
import { Control, Controller } from 'react-hook-form';
import DateInput from '../DateInput';
import moment from 'moment';

/**
 * @param {string} name - The form variable name that is being referenced.
 * @param {string} label - The text caption that will be displayed.
 * @param {Control<any, any>} - Object that is passed to the controller to manage controlled components.
 * @param {Date} defaultValue - Initial value to ensure that each form component has a defined starting value. Optional
 * @param {string} className  - Custom styling. Optional
 * @param {boolean} disabled - Disables input to the component. Optional
 * @param {(date : Date) => void} onDateChange - Callback function that is called whenever the date changes. Optional
 */

interface IControlledDateInputProps {
    name : string;
    label : string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control : Control<any, any>;
    defaultValue ?: Date;
    className ?: string;
    disabled ?: boolean;
    onDateChange ?: (date : Date | moment.Moment) => void;
    type ?: 'date-fns' | 'moment';
}

/**
 * @requires react-hook-form - this component is only meant to be used with react-hook-form
 */
const ControlledDateInput = (props : IControlledDateInputProps) : React.ReactElement => {

    const { type, defaultValue, control, name, onDateChange, label, className, disabled } = props;

    let parsedDate : Date | moment.Moment | null | undefined;
    let defaultDate : Date | moment.Moment | null;

    if (type === 'date-fns' && !moment.isMoment(defaultValue)) {
        parsedDate = new Date(defaultValue ?? new Date());
        defaultDate = isNaN(parsedDate.getTime()) ? null : parsedDate;
    } else {
        parsedDate = defaultValue ?? moment(moment.now());
        defaultDate = !moment.isMoment(parsedDate) ? null : parsedDate;
    }

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultDate}
            render={({ field, fieldState }) => {
                const { ref, onChange: fieldOnChange, value, onBlur } = field;
                void ref; // disposing of ref to fix warning

                const handleDateChange = (date : Date | moment.Moment) => {
                    // Ensure the form state is updated
                    if (type === 'date-fns' && !moment.isMoment(date)) {
                        fieldOnChange(date as Date);
                    } else {
                        const asMoment = moment.isMoment(date) ? date : moment(date);
                        fieldOnChange(asMoment.toDate());
                    }
                    // Invoke optional external callback
                    if (onDateChange) {
                        onDateChange(date);
                    }
                };

                return <DateInput
                    value={value as unknown as Date | moment.Moment | null}
                    onBlur={onBlur}
                    label={label}
                    className={className}
                    error={fieldState.error?.message ?? ''}
                    disabled={disabled}
                    onDateChange={handleDateChange}
                    type={type}
                />;
            }}
        />
    );
};

export default ControlledDateInput;
